import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Menu, AlertTriangle, Crown, Building2 } from 'lucide-react';
import { useAuth, usePermissions } from '@/contexts/AuthContext';
import { useChatLayout } from '@/components/layout/ChatLayout';
import WorkspacePill from '@/components/layout/WorkspacePill';
import { workspaceApi } from '@/services/workspace-api';
import { teamApi } from '@/services/team-api';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import DangerConfirmModal from '@/components/settings/DangerConfirmModal';
import type { ImpactItem } from '@/components/settings/DeletionImpactPreview';

/**
 * Account-closure hub (P3). Role-aware: an owner closes the whole workspace (or
 * transfers ownership as the alternative); anyone else deletes their own account.
 * Deletion is presented as immediate and permanent — the 30-day retention grace
 * is internal-only and never surfaced here.
 */
const AccountClosurePage: React.FC = () => {
  const { user, workspace, memberships, logout } = useAuth();
  const { isOwner } = usePermissions();
  const { isMobile, toggleSidebar } = useChatLayout();
  const navigate = useNavigate();

  const [modal, setModal] = useState<null | 'close' | 'delete'>(null);

  // Members losing access — prefetched so the close-workspace impact preview is
  // ready the moment the owner opens the modal. Owner-relevant only.
  const usersQuery = useQuery({
    queryKey: ['orgUsers'],
    queryFn: teamApi.listUsers,
    enabled: isOwner,
  });

  if (!workspace || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // OAuth-only users have no password to re-verify — the backend skips the password
  // check for them and relies on the typed confirmation alone.
  const requirePassword = user.authProvider !== 'google' && user.authProvider !== 'apple';

  const activeMembership = memberships.find((m) => m.workspace.id === workspace.id);
  const businessCount = activeMembership?.organizations.length ?? 0;
  const memberCount = usersQuery.data?.total ?? usersQuery.data?.users.length;

  const closeImpact: ImpactItem[] = [
    {
      label: businessCount === 1 ? 'Business deleted' : 'Businesses deleted',
      value: businessCount,
    },
    {
      label: memberCount === 1 ? 'Member loses access' : 'Members lose access',
      value: usersQuery.isLoading ? '—' : (memberCount ?? 0),
      emphasize: true,
    },
  ];

  const endSession = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleClose = async (password: string) => {
    await workspaceApi.close(password);
    toast.success('Workspace closed.');
    endSession();
  };

  const handleDelete = async (password: string) => {
    const res = await workspaceApi.deleteAccount(password);
    toast.success(res.message);
    endSession();
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/settings"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Settings
          </Link>
          <WorkspacePill />
        </div>

        <div>
          <h1 className="text-3xl font-bold text-foreground">Close account</h1>
          <p className="text-muted-foreground mt-1">
            Permanently delete your account or close the entire workspace. These actions can&apos;t be undone.
          </p>
        </div>

        {isOwner ? (
          <>
            <section className="rounded-xl border border-destructive/30 bg-destructive/[0.04] p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-base font-semibold text-foreground">Close this workspace</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Closing <strong className="text-foreground">{workspace.name}</strong> ends access for every
                    member and permanently deletes all of its businesses, connected sources, and data.
                  </p>
                </div>
              </div>

              {businessCount > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-muted border border-border p-3 text-sm text-muted-foreground">
                  <Building2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    This workspace still has {businessCount}{' '}
                    {businessCount === 1 ? 'business' : 'businesses'} — closing the workspace deletes{' '}
                    {businessCount === 1 ? 'it' : 'them'} too. You can review{' '}
                    {businessCount === 1 ? 'it' : 'them'} on the{' '}
                    <Link to="/businesses" className="font-medium text-primary hover:underline">
                      Businesses
                    </Link>{' '}
                    page.
                  </span>
                </div>
              )}

              <Button variant="destructive" onClick={() => setModal('close')}>
                Close workspace…
              </Button>
            </section>

            <section className="rounded-xl bg-muted border border-border p-6">
              <div className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-warn shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-semibold text-foreground">
                    Want to leave but keep the workspace running?
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Transfer ownership to a teammate from{' '}
                    <Link to="/settings/team" className="font-medium text-primary hover:underline">
                      Team members
                    </Link>
                    , then you can delete your own account.
                  </p>
                </div>
              </div>
            </section>
          </>
        ) : (
          <section className="rounded-xl border border-destructive/30 bg-destructive/[0.04] p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-semibold text-foreground">Delete my account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  You&apos;ll be removed from <strong className="text-foreground">{workspace.name}</strong> and
                  your account will be permanently deleted. Anything only you can access will need to be
                  re-granted to someone else first.
                </p>
              </div>
            </div>

            <Button variant="destructive" onClick={() => setModal('delete')}>
              Delete my account…
            </Button>
          </section>
        )}
      </div>

      {modal === 'close' && (
        <DangerConfirmModal
          title="Close workspace"
          intro={
            <>
              This permanently closes <strong>{workspace.name}</strong> and deletes all of its data for
              everyone. This can&apos;t be undone.
            </>
          }
          impact={closeImpact}
          confirmWord={workspace.name}
          confirmButtonText="Close workspace"
          requirePassword={requirePassword}
          onConfirm={handleClose}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'delete' && (
        <DangerConfirmModal
          title="Delete my account"
          intro={
            <>
              This permanently deletes your account and removes you from <strong>{workspace.name}</strong>. This
              can&apos;t be undone.
            </>
          }
          confirmWord="DELETE"
          confirmButtonText="Delete my account"
          requirePassword={requirePassword}
          onConfirm={handleDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default AccountClosurePage;
